import React from 'react';
import styled from 'styled-components';
import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Row,
  Upload,
  Alert,
  GetProp,
  UploadFile,
  UploadProps
} from 'antd';
import {
  MinusCircleOutlined,
  PlusOutlined,
  UploadOutlined
} from '@ant-design/icons';
import get from 'lodash/get';
import { I18n } from 'react-redux-i18n';

import { useSubmissions } from 'apiTyped/hooks/submission.query';
import { useUserMe } from 'apiTyped/hooks/users.query';
import { postSubmission } from 'api/negociant';
import { APIErrorsMessage } from 'constants/negociant';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const { TextArea } = Input;

interface AntUploadFile {
  file: UploadFile;
  fileList: UploadFile[];
}

interface SubmissionType {
  file: AntUploadFile | null;
  comment: string;
}

const Component = styled.div`
  .ant-btn.ant-btn-primary {
    width: 150px;
  }

  .ant-card {
    border-radius: 8px;
    height: 86px;
  }

  .ant-card-body {
    padding: 24px 12px 0 12px;
  }

  .ant-card.ant-card-input .ant-card-body {
    padding: 0;
    height: 100%;
  }

  .ant-card.ant-card-input .ant-card-body .ant-input {
    height: 100%;
    border-radius: 8px;
  }

  .ant-card-input .ant-form-item,
  .ant-card-input .ant-input,
  .ant-card-input .ant-form-item-row,
  .ant-card-input .ant-form-item-control,
  .ant-card-input .ant-form-item-control-input,
  .ant-card-input .ant-form-item-control-input-content {
    margin: 0;
    height: 100% !important;
    max-height: 100% !important;
    border: none;
  }

  .ant-form-item .ant-form-item-explain-error {
    text-align: center;
  }

  .clip-icon {
    margin: 5px 5px 0 0;
  }

  .ant-upload-wrapper
    .ant-upload-list
    .ant-upload-list-item
    .ant-upload-list-item-name {
    opacity: 0.5;
    font-family: ${(props) => props.theme.fonts.sfcompact};
    text-align: center;
  }

  .file_uploaded
    .ant-upload-wrapper
    .ant-upload-list
    .ant-upload-list-item
    .ant-upload-list-item-name {
    color: #52c41a;
  }

  .ant-upload-wrapper .ant-upload-select {
    display: flex;
    justify-content: center;
  }

  .ant-upload-wrapper .ant-upload-list .ant-upload-list-item {
    margin-top: 4px;
  }

  .ant-alert {
    border-radius: 4px;
    font-family: ${(props) => props.theme.fonts.sfcompact};
    margin-bottom: 10px;
  }
`;

const FormFiles = () => {
  const { user } = useUserMe();
  const is_enabled = get(user, 'client.id', 0) !== 0;
  const { refetchSubmissions } = useSubmissions(is_enabled);

  const [form] = Form.useForm();
  const [files, setFiles] = React.useState<any[]>([null]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [showSuccessAlert, setShowSuccessAlert] =
    React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<string[]>([]);

  const clearAlerts = () => {
    setShowSuccessAlert(false);
    setErrors([]);
  };

  const onChangeFileList = (e: AntUploadFile | null, index: number) => {
    clearAlerts();

    const file = e?.fileList?.[0] || null;
    const newFiles = [...files];

    if (index >= newFiles.length) {
      newFiles.push(null);
      index = newFiles.length;
    }
    newFiles.fill(file, index, index + 1);

    setFiles(newFiles);
  };

  const onRemoveUploadFile = (indexFile: number) => {
    clearAlerts();
    let newFiles = [...files];
    newFiles = newFiles.filter(
      (file: UploadFile, index: number) => index !== indexFile
    );
    setFiles(newFiles);
  };

  const checkFile = (_: any, value: AntUploadFile) => {
    clearAlerts();
    if (value && value.fileList.length > 0) {
      return Promise.resolve();
    }

    return Promise.reject(
      new Error(I18n.t('negoce.submissions.formFiles.inputEmptyMessage'))
    );
  };

  const getResponseMessageError = (response: any) => {
    if (!response || response.file_id) return null;

    const responseErrors = response?.data?.errors;
    if (responseErrors && responseErrors.length > 0) {
      const labelError = responseErrors[0];
      if (APIErrorsMessage.includes(labelError)) {
        return I18n.t(
          `dataSales.formFiles.inputAPIErrorsMessage.${labelError}`
        );
      }
      return labelError;
    }
    return 'Erreur interne';
  };

  const postFiles = async (values: { items: SubmissionType[] }) => {
    const submissionFiles = values.items.map((item) => {
      if (!item.file || item.file.fileList.length === 0) {
        return null;
      }

      return {
        import_comment: item?.comment || null,
        file: item.file.fileList[0]
      };
    });

    const promises = submissionFiles.map(async (submission) => {
      if (!submission) {
        return null;
      }

      const formData = new FormData();
      formData.append('file', submission.file.originFileObj as FileType);
      if (submission.import_comment) {
        formData.append('import_comment', submission.import_comment);
      }

      return postSubmission({}, formData);
    });

    const responses = await Promise.all(promises).catch((error: any) => {
      console.error(error);
      return [];
    });

    const filesWithStatus = responses.map((response, index) => ({
      ...files[index],
      status: response?.data?.file_id ? 'done' : 'error',
      message: getResponseMessageError(response)
    }));

    const filesDone = filesWithStatus.filter((file) => file.status === 'done');
    setShowSuccessAlert(filesDone.length > 0);
    setErrors(
      filesWithStatus
        .filter((file) => file.status !== 'done')
        .map((file) => [file.name, ':', file.message].join(' '))
    );

    setFiles([]);
  };

  const onSubmit = () => {
    clearAlerts();
    setLoading(true);

    form
      .validateFields()
      .then(postFiles)
      .then(async () => {
        form.resetFields();
        await refetchSubmissions();
      })
      .catch((errorInfo) => console.error(errorInfo))
      .finally(() => setLoading(false));
  };

  return (
    <Component>
      {showSuccessAlert && (
        <Alert
          type="success"
          message={I18n.t('negoce.submissions.formFiles.inputSuccessMessage')}
        />
      )}
      {errors.map((error: string, index: number) => (
        <Alert key={index} message={error} type="error" />
      ))}
      <Form
        form={form}
        name="dynamic_form_complex"
        autoComplete="off"
        initialValues={{ items: [{}] }}
        disabled={loading}
      >
        <Form.List name="items">
          {(fields, { add, remove }) => (
            <div
              style={{
                display: 'flex',
                rowGap: 16,
                flexDirection: 'column'
              }}
            >
              {fields.map((field) => (
                <Row key={field.key} gutter={[16, 10]}>
                  <Col span={12}>
                    <Card className="ant-card-input">
                      <Form.Item name={[field.name, 'comment']}>
                        <TextArea
                          variant="outlined"
                          autoSize={{ minRows: 3, maxRows: 3 }}
                          placeholder={I18n.t(
                            'negoce.submissions.formFiles.inputCommentLabel'
                          )}
                        />
                      </Form.Item>
                    </Card>
                  </Col>
                  <Col span={10}>
                    <Card>
                      <Form.Item
                        name={[field.name, 'file']}
                        style={{ margin: 0 }}
                        rules={[{ validator: checkFile }]}
                        className={
                          files[field.name] &&
                          files[field.name].status === 'done'
                            ? 'file_uploaded'
                            : ''
                        }
                      >
                        <Upload
                          beforeUpload={() => false}
                          maxCount={1}
                          multiple={false}
                          fileList={
                            files[field.name] ? [files[field.name]] : []
                          }
                          onChange={(e) => onChangeFileList(e, field.name)}
                          disabled={
                            (files[field.name] &&
                              files[field.name].status === 'done') ||
                            loading
                          }
                          showUploadList={{
                            showRemoveIcon: !(
                              files[field.name] &&
                              files[field.name].status === 'done'
                            )
                          }}
                          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        >
                          <Button
                            type="primary"
                            shape="round"
                            className="export-button"
                            icon={<UploadOutlined className="download" />}
                          >
                            {I18n.t(
                              'negoce.submissions.formFiles.buttonUploadText'
                            )}
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Card>
                  </Col>
                  <Col
                    span={2}
                    style={{ justifyContent: 'center', display: 'flex' }}
                  >
                    <MinusCircleOutlined
                      className="dynamic-delete-button"
                      onClick={() => {
                        remove(field.name);
                        onRemoveUploadFile(field.name);
                      }}
                    />
                  </Col>
                </Row>
              ))}
              <Button
                type="dashed"
                shape="round"
                icon={<PlusOutlined />}
                onClick={() => {
                  add();
                  const newFiles = [...files];
                  newFiles.push(null);
                  setFiles(newFiles);
                }}
                block
              >
                {I18n.t('negoce.submissions.formFiles.buttonAddText')}
              </Button>
            </div>
          )}
        </Form.List>
        <Divider />
        <Form.Item>
          <Flex justify="center" align="middle">
            <Button
              type="primary"
              shape="round"
              loading={loading}
              onClick={onSubmit}
            >
              {I18n.t('negoce.submissions.formFiles.buttonSaveText')}
            </Button>
          </Flex>
        </Form.Item>
      </Form>
    </Component>
  );
};

export default FormFiles;
